import { useEffect, useRef, useState } from "react";
import { useSearch } from "wouter";
import { Lock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Teller Connect widget type (loaded from CDN)
declare global {
  interface Window {
    TellerConnect?: {
      setup: (opts: {
        applicationId: string;
        environment?: string;
        onSuccess: (enrollment: TellerEnrollment) => void;
        onExit?: () => void;
        onFailure?: (error: { message: string }) => void;
      }) => { open: () => void };
    };
  }
}

interface TellerEnrollment {
  accessToken: string;
  enrollment: {
    id: string;
    institution: { name: string };
  };
  user: { id: string };
  accounts: { id: string }[];
}

type PageState = "loading" | "ready" | "connecting" | "submitting" | "done" | "expired" | "error";

interface TokenInfo {
  accountName: string | null;
  sourceName: string | null;
  appId: string;
  tellerEnv: string;
}

export default function ConnectBank() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get("token") ?? "";

  const [state, setState] = useState<PageState>("loading");
  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!token) {
      setState("expired");
      return;
    }
    fetch(`/api/connect-bank/${token}`)
      .then(async (res) => {
        if (res.status === 410) { setState("expired"); return; }
        if (!res.ok) { setState("error"); setErrorMsg("Invalid or expired link."); return; }
        const data: TokenInfo = await res.json();
        setInfo(data);
        setState("ready");
      })
      .catch(() => { setState("error"); setErrorMsg("Network error. Please try again."); });
  }, [token]);

  function loadTellerScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (scriptLoaded.current && window.TellerConnect) { resolve(); return; }
      const script = document.createElement("script");
      script.src = "https://cdn.teller.io/connect/connect.js";
      script.onload = () => { scriptLoaded.current = true; resolve(); };
      script.onerror = () => reject(new Error("Failed to load Teller Connect"));
      document.body.appendChild(script);
    });
  }

  async function handleConnect() {
    if (!info?.appId) {
      setState("error");
      setErrorMsg("Bank connection not configured. Please contact your accountant.");
      return;
    }
    setState("connecting");
    try {
      await loadTellerScript();
    } catch {
      setState("error");
      setErrorMsg("Could not load the bank connection widget. Please try again.");
      return;
    }

    if (!window.TellerConnect) {
      setState("error");
      setErrorMsg("Could not load the bank connection widget. Please try again.");
      return;
    }

    const tc = window.TellerConnect.setup({
      applicationId: info.appId,
      environment: info.tellerEnv as "sandbox" | "development" | "production",
      onSuccess: (enrollment) => {
        submitEnrollment(enrollment);
      },
      onExit: () => {
        setState("ready");
      },
      onFailure: (err) => {
        setState("error");
        setErrorMsg(err?.message ?? "Bank connection failed. Please try again.");
      },
    });

    tc.open();
  }

  async function submitEnrollment(enrollment: TellerEnrollment) {
    setState("submitting");
    try {
      const accountId = enrollment.accounts?.[0]?.id ?? enrollment.user?.id;
      const res = await fetch(`/api/connect-bank/${token}/enrollment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enrollmentId: enrollment.enrollment.id,
          accessToken: enrollment.accessToken,
          accountId,
          institution: enrollment.enrollment.institution.name,
        }),
      });
      if (res.ok) {
        setState("done");
      } else {
        const data = await res.json().catch(() => ({}));
        setState("error");
        setErrorMsg(data.error ?? "Failed to save connection. Please try again.");
      }
    } catch {
      setState("error");
      setErrorMsg("Network error. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      {/* Header */}
      <div className="mb-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Lock className="w-5 h-5 text-navy-700" style={{ color: "#1e3a5f" }} />
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Carpenter &amp; Company, CPAs, PC
          </span>
        </div>
        <p className="text-xs text-gray-500">Secure bank connection portal</p>
      </div>

      <Card className="w-full max-w-md shadow-md border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-center">
            {state === "loading" && "Loading…"}
            {state === "ready" && "Connect Your Bank"}
            {state === "connecting" && "Opening Bank Connection…"}
            {state === "submitting" && "Saving Connection…"}
            {state === "done" && "Connected!"}
            {state === "expired" && "Link Unavailable"}
            {state === "error" && "Something Went Wrong"}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Loading */}
          {state === "loading" && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          )}

          {/* Ready */}
          {state === "ready" && info && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 text-center">
                {info.sourceName ? (
                  <>
                    Your accountant is requesting access to your{" "}
                    <span className="font-medium">{info.sourceName}</span> account.
                  </>
                ) : (
                  "Your accountant is requesting access to your bank account."
                )}
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-xs text-blue-700">
                Your credentials are never shared with Carpenter &amp; Company. You connect directly
                through your bank's secure portal.
              </div>
              <Button
                className="w-full bg-[#1e3a5f] hover:bg-[#162e4d] text-white"
                onClick={handleConnect}
              >
                Connect Bank Account
              </Button>
            </div>
          )}

          {/* Connecting / submitting */}
          {(state === "connecting" || state === "submitting") && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">
                {state === "connecting"
                  ? "Opening your bank's connection window…"
                  : "Saving your connection…"}
              </p>
            </div>
          )}

          {/* Done */}
          {state === "done" && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
              <div>
                <p className="font-medium text-gray-800">Bank connected successfully.</p>
                <p className="text-sm text-gray-500 mt-1">You can close this window.</p>
              </div>
            </div>
          )}

          {/* Expired */}
          {state === "expired" && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="w-10 h-10 text-amber-400" />
              <div>
                <p className="font-medium text-gray-800">This link has expired or already been used.</p>
                <p className="text-sm text-gray-500 mt-1">
                  Please contact Carpenter &amp; Company for a new link.
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <div>
                <p className="font-medium text-gray-800">Something went wrong.</p>
                <p className="text-sm text-gray-500 mt-1">{errorMsg}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setState("ready")}>
                Try again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="mt-6 text-xs text-gray-400 text-center max-w-xs">
        This connection is powered by Teller. Your bank credentials are never stored by Carpenter &amp; Company.
      </p>
    </div>
  );
}
